import { EXCLUDED_OPSIUM_POLICIES, OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { ReservedUserGroupName } from '@/modules/common/types/reserved-user-group-name';

import { UserGroupPermissions } from '../types/permission';
import { USER_GROUP_NAME_MAX_LENGTH } from '../validations/user-group-validation';

/**
 * Identifies the Super Admin group, whose policies cannot be modified.
 *
 * Matched by its reserved name because that is exactly how the backend decides: the policies
 * update rejects a payload containing Super Admin AND rejects one missing any other group.
 *
 * The name is safe to match on: Super Admin is a reserved name, so no user group can take it —
 * the rename endpoint rejects both renaming Super Admin and renaming anything to that name.
 * It is also the only signal available, since the API sends no protection flags with a group.
 */
export const isSuperAdminUserGroup = (group: Pick<UserGroupPermissions, 'name'>): boolean =>
  group.name === ReservedUserGroupName.SUPER_ADMIN;

/** An organization must always keep at least this many user groups, Super Admin included. */
export const MIN_ORGANIZATION_USER_GROUPS = 2;

/** Every group except the reserved Super Admin can be renamed. */
export const canRenameUserGroup = (group: Pick<UserGroupPermissions, 'name'>): boolean =>
  !isSuperAdminUserGroup(group);

/**
 * Any group can become the organization's default except Super Admin (the backend rejects it) and
 * the group that already is the default (nothing to do).
 */
export const canSetUserGroupAsDefault = (
  group: Pick<UserGroupPermissions, 'name' | 'isDefault'>
): boolean => !isSuperAdminUserGroup(group) && !group.isDefault;

interface CanDeleteUserGroupParams {
  group: Pick<UserGroupPermissions, 'name'>;
  /** Every group in the organization, Super Admin included. */
  allGroups: Pick<UserGroupPermissions, 'name'>[];
}

/**
 * Super Admin is never deletable, and the backend refuses any delete that would drop the
 * organization below `MIN_ORGANIZATION_USER_GROUPS` — so one group beyond the minimum has to
 * exist before anything can go.
 *
 * That same threshold is what guarantees the delete modal has a replacement to offer: its
 * options exclude both the group being deleted and Super Admin, leaving `allGroups.length - 2`.
 *
 * Deleting the DEFAULT group is deliberately allowed and must stay that way: the backend transfers
 * the default marker to the replacement group in the same transaction, so the organization still
 * ends up with exactly one default. Do not add an `isDefault` guard here.
 */
export const canDeleteUserGroup = ({ group, allGroups }: CanDeleteUserGroupParams): boolean =>
  !isSuperAdminUserGroup(group) && allGroups.length > MIN_ORGANIZATION_USER_GROUPS;

/** i18n keys for the group actions live under this namespace. */
const GROUP_MENU_I18N_PREFIX = 'pages.permissions.groupMenu';

interface GroupMenuDisabledReasonsParams {
  group: Pick<UserGroupPermissions, 'name' | 'isDefault'>;
  /** Super Admin included. */
  allGroups: Pick<UserGroupPermissions, 'name'>[];
  /** Resolves an i18n key, so this module decides WHICH copy applies without depending on i18n. */
  translate: (key: string) => string;
}

/** A reason means the action is unavailable; `undefined` means it can be used. */
export interface GroupMenuDisabledReasons {
  rename?: string;
  delete?: string;
  setAsDefault?: string;
}

/**
 * Resolves why each action in a group's menu is unavailable.
 */
export const getGroupMenuDisabledReasons = ({
  group,
  allGroups,
  translate,
}: GroupMenuDisabledReasonsParams): GroupMenuDisabledReasons => {
  if (isSuperAdminUserGroup(group)) {
    return {
      rename: translate(`${GROUP_MENU_I18N_PREFIX}.renameDisabledSuperAdmin`),
      delete: translate(`${GROUP_MENU_I18N_PREFIX}.deleteDisabledSuperAdmin`),
      setAsDefault: translate(`${GROUP_MENU_I18N_PREFIX}.setAsDefaultDisabledSuperAdmin`),
    };
  }

  const reasons: GroupMenuDisabledReasons = {};

  if (!canDeleteUserGroup({ group, allGroups })) {
    reasons.delete = translate(`${GROUP_MENU_I18N_PREFIX}.deleteDisabledMinGroups`);
  }

  if (!canSetUserGroupAsDefault(group)) {
    reasons.setAsDefault = translate(
      `${GROUP_MENU_I18N_PREFIX}.setAsDefaultDisabledAlreadyDefault`
    );
  }

  return reasons;
};

/**
 * Policies to seed a duplicate of `group` with.
 *
 * A FullAccess group cannot simply be copied verbatim: `transformFromPermissionRows`
 * preserves FullAccess for any group that holds it, so the copy would render every box
 * checked while silently discarding every untick the user made. Expanding FullAccess into
 * the concrete policy list keeps the copy editable and shows the same granted state.
 *
 * The expansion deliberately spans feature-flagged policies too (they are carried over
 * untouched on save) but not `EXCLUDED_OPSIUM_POLICIES`, which no editable group can hold
 * past its next save.
 */
export const resolveDuplicateGroupPolicies = (group: UserGroupPermissions): OpsiumPolicy[] => {
  if (!group.policies.includes(OpsiumPolicy.FullAccess)) return group.policies;

  return Object.values(OpsiumPolicy).filter(policy => !EXCLUDED_OPSIUM_POLICIES.includes(policy));
};

interface BuildDuplicateGroupNameParams {
  /** Names already taken in the organization */
  existingNames: string[];
  /** Renders the candidate for a given copy index: 0 -> "X copy", 1 -> "X copy 2", … */
  formatCandidate: (copyIndex: number) => string;
}

/**
 * Picks the first unused "<name> copy" variant so the duplicate modal opens on a valid name
 * rather than an immediate duplicate-name error.
 */
export const buildDuplicateGroupName = ({
  existingNames,
  formatCandidate,
}: BuildDuplicateGroupNameParams): string => {
  const takenNames = new Set(existingNames.map(name => name.trim().toLowerCase()));

  let copyIndex = 0;
  let candidate = formatCandidate(copyIndex).slice(0, USER_GROUP_NAME_MAX_LENGTH);

  // Bounded by the number of existing groups: each iteration rules one of them out
  while (takenNames.has(candidate.toLowerCase()) && copyIndex <= existingNames.length) {
    copyIndex += 1;
    candidate = formatCandidate(copyIndex).slice(0, USER_GROUP_NAME_MAX_LENGTH);
  }

  return candidate;
};
